import { useEffect, useRef, useState } from "react"
import { useQuery } from "react-query"
import dayjs from "dayjs"
import axios from "axios"
import { debouncer, getQueryParams, roundFinalPrice } from "utils/jsUtils"
import FormItem from "components/FormTable/FormItem"
import useCreateLoadStore from "stores/createLoadStore"
import cn from "classnames"

const fetchPrice = (data: any) => async () => {
  if (!data?.customer_id || !data?.location_id || !data?.product) return null
  const params = {
    effective_at: data?.effectiveDateText,
    customer: data?.customer_id,
    location: data?.location_id,
    pickups: [
      {
        product: data?.product,
        terminal: data?.terminal || null,
        ...(data?.supplier && { supplier: data?.supplier }),
      },
    ],
  }

  const { data: pricesData }: any = await axios.post(
    `best-buy/load-prices/`,
    params
  )

  const price = pricesData?.prices?.[0]
  const costBasis = price?.cost_basis ?? null

  if (!price || !price?.price_with_fees) return null

  const freightRate = price?.freight_rate || 0

  return {
    finalPrice: (price.price_with_fees + freightRate).toFixed(4),
    freightRate,
    costBasis,
    floatingPrice: pricesData?.floating_price,
  }
}

const PriceInput = (props: {
  label: string
  supplier: number
  terminal: number
  product: number
  loadingNumber: number
  hideLabel?: boolean
  index?: number
  placeholder?: string
  containerClass?: string
  value: string
  text_value: string
  defaultValue?: string | number
  quantity: string | number
  customerId?: string | null
  locationId?: string | number | null
  onChangeImmediate?: (e: any) => void
  onChange?: (e: any) => void
}) => {
  // const { p_customer_id: customer_id, p_location_id: location_id } =
  //   getQueryParams()
  // const customerId = customer_id ?? props?.customerId
  // const locationId = location_id ?? props?.locationId
  const [hasErrors, setHasErrors] = useState<boolean>(false)
  const [value, setValue] = useState<string>(props.value ?? "")

  const effectiveDateText = dayjs(new Date()).format("YYYY-MM-DD HH:mmZ")
  const customerId = props?.customerId
  const locationId = props?.locationId
  const setFloatingPrice = useCreateLoadStore((s) => s.setFloatingPrice)

  const {
    data: priceObject,
    refetch,
    isRefetching,
    isLoading,
  } = useQuery(
    [
      "pricing",
      customerId,
      locationId,
      props.supplier,
      props.terminal,
      props.product,
      effectiveDateText,
    ],
    fetchPrice({
      ...props,
      customer_id: customerId,
      location_id: locationId,
      effectiveDateText,
    }),
    { refetchOnWindowFocus: false, enabled: false }
  )
  const hasWarnings = String(value)?.length > 0 && parseInt(props?.value) === 0

  const data = priceObject?.finalPrice

  const getPrice = async () => {
    if (!props.product) return
    refetch()
  }

  const onPriceValidations = (e: any) => {
    const rawValue = e?.target?.value
    if (parseInt(rawValue) === 0) {
      onChange(String(rawValue))
      return
    }

    if (!rawValue) {
      setHasErrors(false)
      return
    }
    const priceRounded = roundFinalPrice(rawValue)
    if (!priceRounded || !parseFloat(priceRounded)) {
      setHasErrors(true)
      return
    }
    setHasErrors(false)
    onChange(priceRounded)
  }

  // Allow only numbers, commas, and dots
  const handleKeyDown = (e: any) => {
    const allowedKeys = ["Backspace", "Delete", "ArrowLeft", "ArrowRight"]
    if (!/[\d,\.]/.test(e.key) && !allowedKeys.includes(e.key)) {
      e.preventDefault()
    }
  }

  useEffect(() => {
    debouncer(() => {
      getPrice()
    }, 200)()
  }, [props.supplier, props.terminal, props.product, props.loadingNumber])

  const onChange = (amount: string) => {
    const freightRate = !!parseFloat(amount)
      ? parseFloat((priceObject?.freightRate ?? 0).toString())
      : 0
    const value = amount ? (parseFloat(amount) - freightRate).toFixed(4) : ""
    if (props.index === 0) {
      return props.onChange?.({
        target: {
          value: parseFloat(amount).toFixed(4),
          actualValue: value,
          cost_basis: priceObject?.costBasis,
        },
      })
    }

    props.onChange?.({
      target: {
        value: amount ? roundFinalPrice(parseFloat(amount) - freightRate) : "",
        cost_basis: priceObject?.costBasis,
      },
    })
  }

  const getPropValue = () => {
    return !isNaN(parseFloat(props?.value)) ? props?.value : ""
  }

  // useEffect(() => {
  //   if (props?.value === undefined && props.value !== value) return
  //   const newValue = parseInt(props?.value) >= 1 ? getPropValue() : "0"
  //   setValue(newValue)
  // }, [props?.value])

  useEffect(() => {
    if (isRefetching || !customerId || !locationId || !props.product) return
    if (props?.defaultValue || parseInt(String(props?.defaultValue)) === 0) {
      return
    }
    setValue(data)
    onChange(data)
    setFloatingPrice(priceObject?.floatingPrice)
  }, [data])

  if (isRefetching || isLoading) return <div />

  return (
    <FormItem
      containerClass={cn("relative", cn(props.containerClass))}
      label={props.hideLabel ? "" : props.label}
      name={props.label?.toLowerCase() ?? "price"}
      value={value ?? data}
      text_value={value ?? data}
      onChange={(e: any) => {
        props.onChangeImmediate?.(e.target.value)
        setValue(e.target.value)
      }}
      errors={
        hasErrors && {
          price: "Enter a valid price",
        }
      }
      warnings={hasWarnings}
      onBlur={onPriceValidations}
      className={hasWarnings && "!border-yellow-400 pr-3"}
    />
  )
}
export default PriceInput
